import { Injectable } from '@angular/core';
import {map, mergeMap, Observable, of, throwError} from 'rxjs';
import {
  DoorCorners,
  LightPosition,
  LightSource,
  Orientation,
  ScenarioImage, ScenarioImageCustomData,
  ScenarioType
} from '../../models/scenario-image';
import {
  CreateUserScenarioFromUploadGQL, DeleteUserScenarioForAppUserGQL,
  DeviceType,
  DoorType as DoorTypeGQL,
  ExampleScenarioFragment,
  GetExampleImagesGQL,
  GetUserScenarioByShortIdGQL,
  LightPosition as LightPositionGQL,
  UpdateCornersGQL,
  UserScenarioFragment,
  GetUserScenariosForAppUserGQL
} from '../../../generated/graphql';
import { roundPoint} from '../../models/utils';
import {ScenarioImageLoader, UploadScenarioArgs} from './scenario.image.loader';import { DoorType } from '../../models/door';
import {mapDoorTypeFromGraphQlToIntern, mapDoorTypeFromInternToGraphQl} from "../../utils/door-type-mapping";

@Injectable({ providedIn: 'root' })
export class GraphqlScenarioImageLoader extends ScenarioImageLoader {

  constructor(
    private exampleImageQuery: GetExampleImagesGQL,
    private userScenarioForAppUserQuery: GetUserScenariosForAppUserGQL,
    private getUserScenarioByShortIdGQL: GetUserScenarioByShortIdGQL,
    private createUserScenarioMutation: CreateUserScenarioFromUploadGQL,
    private deleteUserScenarioForAppUserGQL: DeleteUserScenarioForAppUserGQL,
    private updateCornersMutation: UpdateCornersGQL,
  ) {
    super();
  }

  loadUserScenarioImageByShortId(shortId: string): Observable<ScenarioImage> {
    return this.getUserScenarioByShortIdGQL.fetch({
      shortId,
    }, {
      fetchPolicy: 'no-cache',
      context: {
        ignoreDefaultErrorHandling: true
      }}).pipe(
      mergeMap(response => {
        if (!response.data || response.errors) {
          return throwError(() => new Error(`could not load user scenario with short id: ${shortId}`));
        }

        return of(this.mapUserScenario(response.data.getUserScenarioByShortId));
      }),
    );
  }

  loadExampleImages({ customerKey, doorType, setNumber, orientation}: { customerKey: string, doorType: DoorType, orientation?: Orientation, setNumber?: number }): Observable<ScenarioImage[]> {
    return this.exampleImageQuery.fetch({
      customerKey,
      doorType: mapDoorTypeFromInternToGraphQl(doorType)!,
      setNumber
    }).pipe(map(ei => {
      const horizontals = ei.data.getExampleImages.horizontals.map(h => this.mapExampleScenario(h));
      const verticals = ei.data.getExampleImages.verticals.map(h => this.mapExampleScenario(h));

      let result = [...horizontals, ...verticals];
      if (orientation === 'HORIZONTAL') {
        result = [...horizontals];
      } else if (orientation === 'VERTICAL') {
        result = [...verticals];
      }

      return result;
    }));
  }

  deleteUserScenarioForAppUser(userScenarioShortId: string): Observable<ScenarioImage[]> {
    return this.deleteUserScenarioForAppUserGQL.mutate({ userScenarioId: userScenarioShortId }).pipe(
      map(response => response.data!.deleteUserScenarioForAppUser.map(this.mapUserScenario.bind(this))),
    )
  }

  loaderUserScenarioForAppUser(): Observable<ScenarioImage[]> {
    return this.userScenarioForAppUserQuery.fetch(undefined, {
      fetchPolicy: 'no-cache'
    }).pipe(
      map(response => response.data.getAppUser.getUserScenarios.map(this.mapUserScenario.bind(this))),
    )
  }

  uploadUserScenario({ customerKey, doorType, deviceType, file }: UploadScenarioArgs): Observable<{ scenarioImage: ScenarioImage, showRefineLayout: boolean }> {
    console.log('uploadUserScenario', customerKey, doorType, deviceType, file);
    return this.createUserScenarioMutation.mutate({
      createUserScenarioParameters: {
        doorType: doorType ? mapDoorTypeFromInternToGraphQl(doorType) : DoorTypeGQL.Garage,
        customerKey,
        fileUpload: file,
        deviceType,
        doorLocation: undefined
      }
    }, {
      fetchPolicy: 'no-cache',
      context: {
        useMultipart: true,
        headers: {
          'apollo-require-preflight': 'true',
        }
      }
    }).pipe(mergeMap(response => {
      if (!response.data || response.errors) {
        return throwError(() => new Error('could not upload image'));
      }

      const newScenarioImage = this.mapUserScenario(response.data.createUserScenario.userScenario)

      return of({
        scenarioImage: newScenarioImage,
        showRefineLayout: response.data.createUserScenario.showCornerRefinement
      });
    }));
  }

  updateCorners(id: string, corners: Array<DoorCorners>, customData?: ScenarioImageCustomData): Observable<ScenarioImage> {
    return this.updateCornersMutation.mutate({
      userScenarioId: id,
      customData: customData ? JSON.stringify(customData) : null,
      corners: corners.map(c => {
        return {
          index: c.index,
          doorType: mapDoorTypeFromInternToGraphQl(c.doorType)!,
          topLeft: roundPoint(c.topLeft),
          topRight: roundPoint(c.topRight),
          bottomRight: roundPoint(c.bottomRight),
          bottomLeft: roundPoint(c.bottomLeft),
        };
      })
    }).pipe(
      mergeMap(response => {
        if (!response.data || response.errors) {
          return throwError(() => new Error('could not update corners'));
        }

        return of(this.mapUserScenario(response.data.refineCorners));
      }),
    );
  }

  private mapUserScenario(userScenarioFragment: UserScenarioFragment): ScenarioImage {
    const customData: ScenarioImageCustomData | undefined = userScenarioFragment.customData ? JSON.parse(userScenarioFragment.customData) : undefined;

    const corners = customData?.clippedDoorCorners || userScenarioFragment.corners.map(c => {
      return {
        ...c,
        doorType: mapDoorTypeFromGraphQlToIntern(c.doorType)!,
      };
    })

    return {
      id: userScenarioFragment.id,
      shortId: userScenarioFragment.shortId,
      doorType: mapDoorTypeFromGraphQlToIntern(userScenarioFragment.doorType)!,
      lightPosition: this.mapLightPosition(userScenarioFragment.lightPosition),
      thumbnailUrl: userScenarioFragment.thumbnailImage.url,
      isWaitingForVisualizationImprovement: !!userScenarioFragment.isWaitingForVisualizationImprovement,
      image: {
        url: userScenarioFragment.image.url,
      },
      shadowImage: userScenarioFragment.shadowImage?.url ? {
        url: userScenarioFragment.shadowImage.url,
      } : undefined,
      corners,
      lightSources: this.mapGraphQlLightSourceParametersToLocal(userScenarioFragment.lightSourceParameters),
      type: ScenarioType.USER,
      wizartRoom: customData?.wizartRoom
    };
  }

  private mapExampleScenario(exampleScenarioFragment: ExampleScenarioFragment): ScenarioImage {
    const customData: ScenarioImageCustomData | undefined = exampleScenarioFragment.customData ? JSON.parse(exampleScenarioFragment.customData) : undefined;

    return {
      id: exampleScenarioFragment.id,
      shortId: exampleScenarioFragment.shortId,
      doorType: mapDoorTypeFromGraphQlToIntern(exampleScenarioFragment.doorType)!,
      lightPosition: this.mapLightPosition(exampleScenarioFragment.lightPosition),
      thumbnailUrl: exampleScenarioFragment.thumbnail.url,
      image: {
        url: exampleScenarioFragment.image.url,
      },
      shadowImage: exampleScenarioFragment.shadowImage?.url ? {
        url: exampleScenarioFragment.shadowImage.url,
      } : undefined,
      corners: customData?.clippedDoorCorners || exampleScenarioFragment.corners.map(c => {
        return {
          ...c,
          doorType: mapDoorTypeFromGraphQlToIntern(c.doorType)!,
        };
      }),
      lightSources: this.mapGraphQlLightSourceParametersToLocal(exampleScenarioFragment.lightSourceParameters),
      type: ScenarioType.EXAMPLE,
      wizartRoom: customData?.wizartRoom
    };
  }

  mapLightPosition(lightPosition: LightPositionGQL): LightPosition {
    return LightPosition[lightPosition];
  }

  private mapGraphQlLightSourceParametersToLocal(lightSourceParameters: Array<{ intensity: number, location: Array<number> }>): Array<LightSource> {
    return lightSourceParameters.map(ls => {
      return {
        intensity: ls.intensity,
        location: {
          x: ls.location[0],
          y: ls.location[1],
        }
      };
    });
  }
}
